import React, { useState, useEffect, useRef, useCallback } from 'react';
import $ from 'jquery';
import Select from 'react-select';
import type { SingleValue, StylesConfig } from 'react-select';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import * as apiHelper from './Utils';
import { nanoid } from 'nanoid';
import RenderInBody from './RenderInBody';
import './ConversationOwner.css';
import ClickOutSide from './ClickOutSide';

// react-select v5 styles matching ComboBoxField appearance
const getOwnerSelectStyles = (isDark: boolean): StylesConfig<Person, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '24px',
    height: '24px',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: '11px',
    backgroundColor: isDark ? '#2d2f2f' : base.backgroundColor,
    borderColor: state.isFocused ? '#62accb' : isDark ? '#4a4c4c' : '#ccc',
    width: '150px',
    '&:hover': {
      borderColor: '#62accb',
    },
  }),
  valueContainer: base => ({
    ...base,
    height: '24px',
    padding: '0 6px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '22px',
    color: isDark ? '#a9a093' : base.color,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '2px',
    width: '20px',
    color: isDark ? '#6e6860' : base.color,
    '&:hover': {
      color: isDark ? '#a9a093' : '#666',
    },
  }),
  placeholder: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: isDark ? '#6e6860' : '#aaa',
  }),
  singleValue: base => ({
    ...base,
    lineHeight: '22px',
    fontSize: '11px',
    color: isDark ? '#a9a093' : '#333',
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    width: '150px',
    zIndex: 99999,
    backgroundColor: isDark ? '#2d2f2f' : '#fff',
    border: isDark ? '1px solid #4a4c4c' : base.border,
    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : base.boxShadow,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '130px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    lineHeight: '1.8',
    padding: '2px 6px',
    minHeight: 'auto',
    backgroundColor: state.isFocused ? '#62accb' : isDark ? '#2d2f2f' : '#fff',
    color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#666666',
    border: state.isFocused ? '1px solid #eee' : '1px solid transparent',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#62accb',
    },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: '11px',
    color: isDark ? '#6e6860' : base.color,
    backgroundColor: isDark ? '#2d2f2f' : base.backgroundColor,
  }),
});

// Types
interface Person {
  PersonID: number;
  Name: string;
  [key: string]: any;
}

interface ConversationLayer {
  LayerId: number;
  [key: string]: any;
}

interface Conversation {
  ConversationId?: number;
  OwnedById?: number;
  OwnedBy?: string;
  IsEnable?: boolean;
  selectedConfigValueId?: string;
  ConfigValueId?: string;
  ProjectID?: string;
  ProjectId?: string;
  ConversationLayers?: ConversationLayer[];
  SocketConnectionId?: string;
  [key: string]: any;
}

interface ApiConfig {
  [key: string]: any;
}

interface Config {
  SocketConnectionId?: string;
  [key: string]: any;
}

interface ConfigData {
  [key: string]: any;
}

interface ConversationOwnerProps {
  apiConfig: ApiConfig;
  itemType?: number;
  conversation: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  onChangedOwner?: (item: Conversation, isFromInfoWindow: boolean) => void;
  onChangedLayers?: (item: Conversation) => void;
  config?: Config;
  configData?: ConfigData;
  groupIdsSelected?: string;
}

const ConversationOwner: React.FC<ConversationOwnerProps> = ({
  apiConfig,
  conversation,
  disableAutoSave = false,
  isNew = false,
  onChangedOwner,
  config,
  configData,
  groupIdsSelected,
}) =>
{
  const containerRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);

  const [elementId] = useState(() => nanoid(8));

  // Determine initial canEdit state
  const getInitialCanEdit = useCallback(() =>
  {
    if (isNew)
    {
      return true;
    }

    const isRecycleBin = window.location.href.indexOf('RecycleBin') > -1;
    let canEditFixed = false;

    if (conversation)
    {
      if (!conversation.IsEnable && isRecycleBin)
      {
        canEditFixed =
          CorgeeStorageHelper.canEditOwner(conversation, CorgeeStorageHelper.personId()) &&
          !!conversation.IsEnable;
      }
      else
      {
        canEditFixed = CorgeeStorageHelper.canEditOwner(
          conversation,
          CorgeeStorageHelper.personId(),
        );
      }
    }

    const personLayers = CorgeeStorageHelper.getAllLayers();
    if (personLayers.length === 1)
    {
      canEditFixed = false;
    }

    return canEditFixed;
  }, [conversation, isNew, configData]);

  const [canEdit, setCanEdit] = useState(getInitialCanEdit);
  const [editorEnabled, setEditorEnabled] = useState(false);
  const [dataPersons, setDataPersons] = useState<Person[]>([]);
  const [selectedId, setSelectedId] = useState<number | string>(conversation?.OwnedById || '');
  const [selectedName, setSelectedName] = useState(conversation?.OwnedBy || 'Not Set');
  const [originalSelectedId, setOriginalSelectedId] = useState<number | string>(
    conversation?.OwnedById || '',
  );
  const [_originalSelectedName, setOriginalSelectedName] = useState(conversation?.OwnedBy || '');
  const [configValueId, setConfigValueId] = useState(
    conversation?.selectedConfigValueId || conversation?.ConfigValueId || '',
  );
  const [projectId, setProjectId] = useState(
    conversation?.ProjectID || conversation?.ProjectId || '',
  );
  const [isFromInfoWindow, setIsFromInfoWindow] = useState(false);
  const [isFromTabDetails, setIsFromTabDetails] = useState(false);
  const [posTop, setPosTop] = useState(0);
  const [posLeft, setPosLeft] = useState(0);
  const [CSSPosition, setCSSPosition] = useState<'fixed' | 'absolute'>('fixed');

  const isConversationDetailsView = window.location.href.indexOf('ConversationDetails') > -1;

  // Latest selection, readable from deferred callbacks (setTimeout / blur) that would
  // otherwise close over a stale render's state.
  const selectionRef = useRef({ selectedId, selectedName, originalSelectedId });
  selectionRef.current = { selectedId, selectedName, originalSelectedId };

  // Readjust position
  const readjustPosition = useCallback(() =>
  {
    const fieldContainer = document.getElementById('field-container-' + elementId);
    if (fieldContainer)
    {
      const fce = fieldContainer.getBoundingClientRect();
      let newPosLeft = fce.left;
      let newPosTop = fce.top;
      let newCSSPosition: 'fixed' | 'absolute' = 'fixed';

      const conversationDetailsViewBody = fieldContainer.closest('.conversationDetailsViewBody');
      if (conversationDetailsViewBody)
      {
        newPosTop = 20;
        newPosLeft = 0;
        newCSSPosition = 'absolute';
      }

      setPosTop(newPosTop);
      setPosLeft(newPosLeft);
      setCSSPosition(newCSSPosition);
    }
  }, [elementId]);

  // Close select
  const onCloseSelect = useCallback(() =>
  {
    setEditorEnabled(false);
  }, []);

  // Save by blur
  const onSaveByBlur = useCallback(
    (override?: { selectedId: number | string; selectedName: string }) =>
    {
      // Clean up tooltips
      const tooltips = document.querySelectorAll('.help-tooltip-item');
      tooltips.forEach(el => el.remove());

      if (containerRef.current)
      {
        const $element = $(containerRef.current);
        $element.closest('.layer-details-container').css('overflow', '');
        $('body').css('overflow', '');

        const infoWindowScrollContainer = $element.closest(
          '.details-tab-directive .card-body-area',
        );
        if (infoWindowScrollContainer.length > 0)
        {
          infoWindowScrollContainer.css('overflow', '');
        }

        $element.closest('.layer-details-container').off('scroll');
        $element.closest('.card-body-area').off('scroll');
        $element.closest('md-dialog-content').off('scroll');
        $element.closest('.add-inline-task').off('scroll');
      }

      const currentId = override ? override.selectedId : selectionRef.current.selectedId;
      const currentName = override ? override.selectedName : selectionRef.current.selectedName;
      const currentOriginalId = selectionRef.current.originalSelectedId;

      if (currentId.toString() !== currentOriginalId.toString())
      {
        if (isNew)
        {
          const itemCopy = { ...conversation };
          itemCopy.OwnedById = currentId as number;
          itemCopy.OwnedBy = currentName;

          if (onChangedOwner)
          {
            onChangedOwner(itemCopy, isFromInfoWindow);
          }

          setOriginalSelectedId(currentId);
          setOriginalSelectedName(currentName);
        }

        if (!disableAutoSave)
        {
          if ((!isNew && Number(currentId) >= 0 && currentId.toString() !== '0') || isNew)
          {
            const itemCopy = { ...conversation };
            itemCopy.OwnedById = currentId as number;
            itemCopy.OwnedBy = currentName;

            setOriginalSelectedId(currentId);
            setOriginalSelectedName(currentName);

            if (onChangedOwner)
            {
              onChangedOwner(itemCopy, isFromInfoWindow);
            }

            itemCopy.SocketConnectionId = config?.SocketConnectionId;

            apiHelper
              .post(apiConfig as any, 'api/conversation/UpdateOwnedConversation', itemCopy, true)
              .catch(error =>
              {
                console.warn('Api Error', error);
              });
          }
        }
      }
      onCloseSelect();
    },
    [
      isNew,
      disableAutoSave,
      conversation,
      onChangedOwner,
      isFromInfoWindow,
      config,
      apiConfig,
      onCloseSelect,
    ],
  );

  // Get list of persons
  const getListPerson = useCallback(
    (requestParams: {
      ConversationId: number;
      ProjectId: string | null;
      ConfigValueId: string;
      LayerIdsSelected: string;
      GroupIdsSelected: string;
    }) =>
    {
      let urlParams = '?configValueId=' + requestParams.ConfigValueId;
      urlParams += '&projectId=' + requestParams.ProjectId;
      urlParams += '&layerIdsSelected=' + requestParams.LayerIdsSelected;
      urlParams += '&groupIdsSelected=' + requestParams.GroupIdsSelected;
      urlParams += '&conversationId=' + requestParams.ConversationId;

      apiHelper
        .get(apiConfig as any, 'api/Person/GetListPeopleBySecurity' + urlParams)
        .then((persons: Person[]) =>
        {
          if (persons.length <= 1)
          {
            setEditorEnabled(false);
          }
          else
          {
            setEditorEnabled(true);
            setDataPersons(persons);
            setTimeout(() =>
            {
              readjustPosition();
              if (selectRef.current)
              {
                selectRef.current.focus();
              }
            });
            return;
          }
          setDataPersons(persons);
        });
    },
    [apiConfig, readjustPosition, elementId, posTop, posLeft, CSSPosition],
  );

  // Enable editor
  const onEnableEditor = useCallback(() =>
  {
    const projectIdValue = conversation.ProjectID || conversation.ProjectId;
    const conversationId = conversation.ConversationId;

    const requestParams = {
      ConversationId: conversationId === undefined || conversationId === null ? 0 : conversationId,
      ProjectId: projectIdValue === undefined ? null : projectIdValue,
      ConfigValueId: conversation.selectedConfigValueId || conversation.ConfigValueId || '',
      LayerIdsSelected:
        conversation.ConversationLayers?.map(miniLayer => miniLayer.LayerId).join(',') || '',
      GroupIdsSelected: groupIdsSelected || '',
    };

    getListPerson(requestParams);
  }, [conversation, groupIdsSelected, getListPerson, isNew, canEdit, editorEnabled, selectedName]);

  // Select owner
  const onSelectOwner = useCallback(
    (itemsSelected: SingleValue<Person>) =>
    {
      const nextId = itemsSelected === null ? 0 : itemsSelected.PersonID;
      const nextName = itemsSelected === null ? 'Not Set' : itemsSelected.Name;

      setSelectedId(nextId);
      setSelectedName(nextName);

      onSaveByBlur({ selectedId: nextId, selectedName: nextName });
    },
    [onSaveByBlur],
  );

  // Apply style
  const onApplyStyle = useCallback(() =>
  {
    setTimeout(() =>
    {
      readjustPosition();
    });

    if (containerRef.current)
    {
      const $element = $(containerRef.current);
      $element.closest('.layer-custom-fields-container').first().on('scroll', onCloseSelect);
    }
  }, [readjustPosition, onCloseSelect]);

  // Selected value for react-select v5 (needs full object, not just ID)
  const selectedValue = dataPersons.find(p => p.PersonID === selectedId) || null;

  // Effect: Component mount
  useEffect(() =>
  {
    if (conversation?.OwnedById)
    {
      setSelectedId(conversation.OwnedById);
      setSelectedName(conversation.OwnedBy || 'Not Set');
      setOriginalSelectedId(conversation.OwnedById);
      setOriginalSelectedName(conversation.OwnedBy || '');
    }

    if (containerRef.current)
    {
      const $containerElement = $(containerRef.current);
      if ($containerElement.closest('.details-tab-directive').length > 0)
      {
        setIsFromInfoWindow(true);
      }
      if ($containerElement.closest('#canvasDetails').length > 0)
      {
        setIsFromTabDetails(true);
      }
    }

    setTimeout(() =>
    {
      readjustPosition();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect: Update when conversation changes
  useEffect(() =>
  {
    if (isNew)
    {
      setCanEdit(true);
    }
    else if (
      conversation?.OwnedById !== undefined &&
      conversation.OwnedById !== originalSelectedId
    )
    {
      setSelectedId(conversation.OwnedById);
      setSelectedName(conversation.OwnedBy || 'Not Set');
      setOriginalSelectedId(conversation.OwnedById);
      setOriginalSelectedName(conversation.OwnedBy || '');

      setCanEdit(getInitialCanEdit());
    }

    if (
      conversation?.selectedConfigValueId &&
      configValueId !== conversation.selectedConfigValueId
    )
    {
      setConfigValueId(conversation.selectedConfigValueId);
    }

    if (conversation?.ProjectId && projectId !== conversation.ProjectId)
    {
      setProjectId(conversation.ProjectId);
    }
  }, [conversation?.OwnedById, conversation?.selectedConfigValueId, conversation?.ProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDark = document.documentElement.classList.contains('dark');
  const ownerSelectStyles = getOwnerSelectStyles(isDark);

  const styleRender: React.CSSProperties = {
    display: 'block',
    zIndex: 99999,
    top: posTop,
    left: posLeft,
    position: CSSPosition,
    fontSize: isFromInfoWindow || isFromTabDetails ? 15 : undefined,
  };

  return (
    <div id={`field-container-${elementId}`} className="click-to-edit" ref={containerRef}>
      {!canEdit && <div className="lbl-non-editable-input">{selectedName}</div>}
      {canEdit &&
        (editorEnabled && !isConversationDetailsView ? (
          <div id={`select-target-owner-${elementId}`}>
            <RenderInBody>
              <div
                id={`select-render-owner-${elementId}`}
                className="comboBoxRef ConversationOwner-hidden-control-container"
                style={styleRender}
              >
                <ClickOutSide onClickOutSide={() => onSaveByBlur()}>
                  <Select<Person, false>
                    ref={selectRef}
                    value={selectedValue}
                    options={dataPersons}
                    menuIsOpen={true}
                    isSearchable={true}
                    closeMenuOnSelect={true}
                    isClearable={true}
                    autoFocus={true}
                    getOptionLabel={opt => opt.Name}
                    getOptionValue={opt => String(opt.PersonID)}
                    onChange={onSelectOwner}
                    onMenuOpen={onApplyStyle}
                    placeholder="Select..."
                    onBlur={() => onSaveByBlur()}
                    styles={ownerSelectStyles}
                  />
                </ClickOutSide>
              </div>
            </RenderInBody>
          </div>
        ) : editorEnabled && isConversationDetailsView ? (
          <div id={`select-target-owner-${elementId}`}>
            <div
              className="comboBoxRef ConversationOwner-hidden-control-container"
              style={styleRender}
            >
              <ClickOutSide onClickOutSide={() => onSaveByBlur()}>
                <Select<Person, false>
                  ref={selectRef}
                  value={selectedValue}
                  options={dataPersons}
                  menuIsOpen={true}
                  isSearchable={true}
                  closeMenuOnSelect={true}
                  isClearable={true}
                  autoFocus={true}
                  getOptionLabel={opt => opt.Name}
                  getOptionValue={opt => String(opt.PersonID)}
                  onChange={onSelectOwner}
                  onMenuOpen={onApplyStyle}
                  placeholder="Select..."
                  onBlur={() => onSaveByBlur()}
                  styles={ownerSelectStyles}
                />
              </ClickOutSide>
            </div>
          </div>
        ) : (
          <div className="lbl-editable-input" onClick={onEnableEditor}>
            {selectedName}
          </div>
        ))}
    </div>
  );
};

export default ConversationOwner;
